import { z } from 'zod';
/**
 * Sites, places, and vendor waypoint aliases (DEV-17247, memory-architecture
 * §5.3) — symbolic localisation, phase 1. Names are the shared vocabulary
 * between the orchestrator and every vendor stack; coordinates stay local to
 * each robot. No coordinate math exists anywhere in this contract, by design:
 * "go to the dock" resolves a NAME in each robot's private frame, the same
 * way VDA 5050 references nav-graph node ids and never metric positions.
 *
 * `Site` resolves `SpecSection.location.mapId` by convention (mapId =
 * Site.id). `Place` supplies the place anchors and the tree distance the
 * anchored-retrieval scorer uses (DEV-17257). `PlaceAlias` is the onboarding
 * primitive: a vendor robot's own waypoint name mapped onto our Place — a
 * config screen, not a survey visit. PlaceEdge (directed traversal edges) is
 * deferred until the routing need is evidenced (DEV-17259).
 */
export const SiteKindSchema = z.enum(['INDOOR', 'OUTDOOR', 'MIXED']);
/**
 * The place kinds v1 ships. A free vocabulary at the DB level (string
 * column) so a customer taxonomy can extend it without a migration; the
 * create input restricts to the known set so a typo is a 400, not a silently
 * unmatchable kind. Exclusion zones are ordinary places with kind 'no_go'.
 */
export const placeKinds = ['zone', 'room', 'aisle', 'dock', 'no_go', 'field_block'];
export const PlaceKindSchema = z.enum(placeKinds);
// ─── Geo + coordinate value types (shared by entities, inputs, and dispatch) ───
/** A geodetic point: latitude/longitude in degrees, optional altitude in metres. */
export const GeoPointSchema = z.object({
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    alt: z.number().optional(),
});
/**
 * Largest |latitude| (degrees) a geo-anchor may sit at. Site-frame <-> GPS conversion
 * divides by cos(latitude) to turn east metres into longitude; that blows up toward the
 * poles, so an anchor past this is rejected rather than converted to a wrong longitude.
 */
export const maxGeoAnchorAbsLatDeg = 89.9;
/** A Site's GPS registration: the geodetic position of site (0,0) and the bearing of site +X (CCW from East, radians). */
export const GeoAnchorSchema = z.object({
    lat: z
        .number()
        .min(-maxGeoAnchorAbsLatDeg, {
        message: `geoAnchor.lat must be within +/-${maxGeoAnchorAbsLatDeg} degrees (polar anchors are not supported)`,
    })
        .max(maxGeoAnchorAbsLatDeg, {
        message: `geoAnchor.lat must be within +/-${maxGeoAnchorAbsLatDeg} degrees (polar anchors are not supported)`,
    }),
    lon: z.number().min(-180).max(180),
    headingRad: z.number(),
});
/** A 2D pose in the site frame: position plus optional heading (radians). */
export const SiteFramePoseSchema = z.object({
    x: z.number(),
    y: z.number(),
    yaw: z.number().optional(),
});
// ─── Entities ────────────────────────────────────────────────────────────────
export const SiteSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    name: z.string(),
    kind: SiteKindSchema,
    /** Optional GPS anchor of the site frame (MAP-5); null on a purely-indoor site. */
    geoAnchor: GeoAnchorSchema.optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
});
export const PlaceSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    siteId: z.string(),
    parentPlaceId: z.string().nullable(),
    /** Serialized wide (string): rows may predate a narrowed create vocabulary. */
    kind: z.string(),
    label: z.string(),
    /** Coordinate layer (MAP-1/MAP-5), additive + optional; symbolic-only consumers ignore it. */
    siteFramePose: SiteFramePoseSchema.optional(),
    geoPoint: GeoPointSchema.optional(),
    perDeviceNativeWaypointId: z.record(z.string(), z.string()).optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
});
export const PlaceAliasSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    placeId: z.string(),
    /** Whose vocabulary: a deviceId, a vendor slug, or 'fleet'. */
    scope: z.string(),
    alias: z.string(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
});
// ─── Inputs ──────────────────────────────────────────────────────────────────
export const CreateSiteInputSchema = z.object({
    name: z.string().min(1).max(200),
    kind: SiteKindSchema,
    geoAnchor: GeoAnchorSchema.optional(),
});
export const UpdateSiteInputSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    kind: SiteKindSchema.optional(),
    geoAnchor: GeoAnchorSchema.nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export const CreatePlaceInputSchema = z.object({
    siteId: z.string().min(1),
    parentPlaceId: z.string().min(1).optional(),
    kind: PlaceKindSchema,
    label: z.string().min(1).max(200),
    siteFramePose: SiteFramePoseSchema.optional(),
    geoPoint: GeoPointSchema.optional(),
    perDeviceNativeWaypointId: z.record(z.string(), z.string()).optional(),
});
export const UpdatePlaceInputSchema = z
    .object({
    parentPlaceId: z.string().min(1).nullable().optional(),
    kind: PlaceKindSchema.optional(),
    label: z.string().min(1).max(200).optional(),
    siteFramePose: SiteFramePoseSchema.nullable().optional(),
    geoPoint: GeoPointSchema.nullable().optional(),
    perDeviceNativeWaypointId: z.record(z.string(), z.string()).nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export const CreatePlaceAliasInputSchema = z.object({
    placeId: z.string().min(1),
    scope: z.string().min(1).max(200),
    alias: z.string().min(1).max(200),
});
// ─── Alias onboarding (the vendor waypoint import flow) ─────────────────────
/**
 * POST body for the alias auto-proposal step: the vendor robot's own
 * waypoint list, verbatim. The service matches each name against the site's
 * Places by NAME SIMILARITY only (normalized token overlap + edit distance —
 * deterministic, zero model calls) and returns ranked proposals for a person
 * to confirm. Nothing is written until the confirm call.
 */
export const ProposePlaceAliasesInputSchema = z.object({
    siteId: z.string().min(1),
    /** Whose vocabulary the aliases will belong to (deviceId / vendor / 'fleet'). */
    scope: z.string().min(1).max(200),
    waypoints: z.array(z.string().min(1).max(200)).min(1).max(500),
});
export const PlaceAliasProposalSchema = z.object({
    /** The vendor waypoint name being matched. */
    alias: z.string(),
    /** Best-match place id, or null when nothing clears the floor. */
    placeId: z.string().nullable(),
    placeLabel: z.string().nullable(),
    /** Similarity in [0,1]; a person confirms — this is a hint, not a decision. */
    confidence: z.number(),
    /** True when an alias row for (scope, alias) already exists. */
    alreadyMapped: z.boolean(),
});
/** Confirm step: exactly the proposals the person accepted. */
export const ConfirmPlaceAliasesInputSchema = z.object({
    siteId: z.string().min(1),
    scope: z.string().min(1).max(200),
    mappings: z
        .array(z.object({ alias: z.string().min(1).max(200), placeId: z.string().min(1) }))
        .min(1)
        .max(500),
});
// ── MAP-1 (DEV-17527): coordinate + registration layer on top of the symbolic Site/Place above.
//    Additive; the symbolic schemas stay coordinate-free. See unified-fleet-map-design.md.
/**
 * site_T_robot as a 2D similarity. Apply as site = scale * R(rotationRad) * robot + (tx, ty).
 * scale is 1 for rigid (metric) robots; only non-1 for scale-ambiguous sources.
 */
export const SiteTransform2DSchema = z.object({
    rotationRad: z.number(),
    scale: z.number().positive(),
    tx: z.number(),
    ty: z.number(),
});
/** One physical location expressed in both the site frame and a robot's own frame. */
export const CorrespondenceSchema = z.object({
    /** The canonical Place this control point is (optional; ad-hoc points may have none). */
    placeId: z.string().optional(),
    site: z.object({ x: z.number(), y: z.number() }),
    robot: z.object({ x: z.number(), y: z.number() }),
});
/** How a device's transform into the site frame was established. */
export const RegistrationMethodSchema = z.enum([
    'fiducial', // surveyed AprilTag / ArUco
    'geo_datum', // geo-anchored GNSS
    'prior_map', // relocalization against a saved map
    'control_points', // shared named waypoints driven on the robot
    'manual', // operator drag/rotate/scale in the UI
    'map_merge', // automatic feature-based grid merge
]);
/** Trustworthiness of a device's current transform. */
export const RegistrationStatusSchema = z.enum([
    'live', // fresh and trustworthy
    'stale', // past its freshness budget; show with a caution
    'needs_reanchor', // a frame reset/jump invalidated it
    'unregistered', // no transform yet; the robot cannot be placed on the map
]);
/**
 * The per-device registration into a site: the transform plus the evidence it was
 * solved from (so it is auditable and re-solvable), a confidence, and a status.
 * The home for this is cortex, keyed by (siteId, deviceId); see DEV-17241.
 */
export const DeviceSiteRegistrationSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    siteId: z.string(),
    deviceId: z.string(),
    /** Which site level this registration is for (multi-floor); null/absent for single-level. */
    level: z.number().int().optional(),
    transform: SiteTransform2DSchema,
    method: RegistrationMethodSchema,
    /** The correspondences the transform was solved from (RMF reference_coordinates pattern). */
    correspondences: z.array(CorrespondenceSchema),
    /** Fit residual (RMSE, site units) and inlier count, for trust/debug. */
    rmse: z.number().nonnegative().optional(),
    inlierCount: z.number().int().nonnegative().optional(),
    /** Isotropic 1-sigma position uncertainty (site units), inflated with time/distance since last fix. */
    positionSigma: z.number().nonnegative().optional(),
    status: RegistrationStatusSchema,
    updatedAt: z.string().datetime(),
    deletedAt: z.string().datetime().nullable().optional(),
});
/**
 * Optional coordinate addition to the existing Place schema. Keep Place name-first;
 * these are additive so symbolic-only consumers are unaffected. Merge these fields
 * into the existing PlaceSchema rather than creating a second schema.
 */
export const PlaceCoordinateFieldsSchema = z.object({
    /** Pose of this Place in the site frame (the one authoritative coordinate). */
    siteFramePose: z.object({ x: z.number(), y: z.number(), yaw: z.number().optional() }).optional(),
    /** Per-device native references: deviceId -> the robot's own saved waypoint id (preferred over transform). */
    perDeviceNativeWaypointId: z.record(z.string(), z.string()).optional(),
    /** An explicit GPS coordinate for this Place (outdoor waypoints; optional for indoor). */
    geoPoint: GeoPointSchema.optional(),
});
/**
 * MAP-1 (DEV-17527) API inputs for creating a device's site registration.
 * Correspondence-based: fiducial / control-points / geo-datum / prior-map all
 * reduce to a set of point correspondences the kernel solves.
 */
/** How a device reports poses, so the platform can normalize to the canonical site convention. */
export const FrameConventionSchema = z.object({
    angleUnit: z.enum(['rad', 'deg']).optional(),
    axes: z.enum(['enu', 'ned', 'xy_down']).optional(),
    metersPerUnit: z.number().positive().optional(),
});
export const RegisterFromCorrespondencesInputSchema = z.object({
    siteId: z.string(),
    deviceId: z.string(),
    level: z.number().int().optional(),
    method: RegistrationMethodSchema,
    correspondences: z.array(CorrespondenceSchema).min(2),
    /** Recover a scale term (monocular / pixel sources). Default rigid. */
    allowScale: z.boolean().optional(),
    /** Reject the fit when its RMSE exceeds this (keep the device unregistered instead). */
    maxRmse: z.number().positive().optional(),
    /**
     * How the robot-frame coordinates in `correspondences` are expressed. Applied
     * to normalize them to the canonical site convention before solving, so the
     * stored transform matches the normalized poses the map view renders.
     */
    convention: FrameConventionSchema.optional(),
});
/** Manual anchor input: the operator supplies the transform directly (no solve). */
export const RegisterManualInputSchema = z.object({
    siteId: z.string(),
    deviceId: z.string(),
    level: z.number().int().optional(),
    transform: SiteTransform2DSchema,
});
// ── MAP-6 (DEV-17632): the backdrop map layer — the structure UNDER the dots. ──
/**
 * Where a backdrop came from. A DISPLAY map in the site frame, decoupled from any
 * robot's live navigation cost map:
 *  - FLOORPLAN: an uploaded floorplan/CAD image (source of record when present).
 *  - OCCUPANCY: a static snapshot of ONE reference robot's SLAM occupancy grid,
 *    anchored into the site frame by that robot's site_T_robot. Never auto-merged.
 *  - TILES: satellite / street tiles, positioned by a geo-anchor (outdoor sites).
 */
export const BackdropTypeSchema = z.enum(['FLOORPLAN', 'OCCUPANCY', 'TILES']);
/**
 * A site's backdrop: ONE display artifact per (site, level), in the site frame,
 * drawn under the robots + waypoints. `assetRef` is the image URL (floorplan or
 * baked occupancy grid) or a tile-template URL. The calibration
 * (metresPerPixel + siteFrameOrigin + rotationRad) is this backdrop's own
 * registration into the site frame — so a pixel maps to a site-frame metre the
 * same way a robot pose does. For TILES, `geoAnchor` positions the layer. The
 * viewer composes it with the Places and live robot poses from the same bundle.
 */
export const SiteBackdropSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    siteId: z.string(),
    /** Which site level this backdrop is for (multi-floor); 0 for single-level. */
    level: z.number().int(),
    type: BackdropTypeSchema,
    /** Operator-chosen source descriptor (an upload id or the reference deviceId), optional. */
    source: z.string().optional(),
    /** Image URL (floorplan / baked occupancy grid) or tile-template URL. */
    assetRef: z.string().optional(),
    widthPx: z.number().int().positive().optional(),
    heightPx: z.number().int().positive().optional(),
    /** Metres per backdrop pixel (image types). */
    metresPerPixel: z.number().positive().optional(),
    /** Pixel coordinate of the site-frame origin (0,0) within the image. */
    siteFrameOriginX: z.number().optional(),
    siteFrameOriginY: z.number().optional(),
    /** Backdrop rotation relative to site +X, radians. */
    rotationRad: z.number(),
    /** For TILES: the geo-anchor that positions the layer (mirrors Site.geoAnchor). */
    geoAnchor: GeoAnchorSchema.optional(),
    version: z.number().int(),
    updatedAt: z.string().datetime(),
    deletedAt: z.string().datetime().nullable().optional(),
});
/**
 * Upsert a site's backdrop (create or replace the live one for a level). The
 * calibration is validated by type at the service layer: FLOORPLAN/OCCUPANCY
 * need assetRef + metresPerPixel + siteFrameOrigin; TILES need assetRef +
 * geoAnchor. Replacing supersedes the prior live backdrop for that level.
 */
export const UpsertSiteBackdropInputSchema = z.object({
    siteId: z.string().min(1),
    level: z.number().int().optional(),
    type: BackdropTypeSchema,
    source: z.string().min(1).max(500).optional(),
    assetRef: z.string().min(1).max(2000).optional(),
    widthPx: z.number().int().positive().optional(),
    heightPx: z.number().int().positive().optional(),
    metresPerPixel: z.number().positive().optional(),
    siteFrameOriginX: z.number().optional(),
    siteFrameOriginY: z.number().optional(),
    rotationRad: z.number().optional(),
    geoAnchor: GeoAnchorSchema.optional(),
});
//# sourceMappingURL=places.js.map